import { createServerFn } from "@tanstack/react-start";

import {
  lookupDeleteInputSchema,
  lookupListInputSchema,
  lookupNameInputSchema,
  lookupPageInputSchema,
  lookupRenameInputSchema,
  manufacturersByIdsInputSchema,
  manufacturerUsageInputSchema,
  tierCreateInputSchema,
  tierUpdateInputSchema,
  type LookupDeleteInput,
  type LookupKindInput,
  type LookupNameInput,
  type LookupPageInput,
  type LookupRenameInput,
  type ManufacturersByIdsInput,
  type ManufacturerUsageInput,
  type TierCreateInput,
  type TierUpdateInput,
} from "@/api/contracts/catalog-admin";
import { requireAuth } from "@/api/middleware/auth";
import type { LookupKind } from "@/lib/catalog-admin";
import type { ResolvedSession } from "@/server/use-cases/auth/resolve-session";
import {
  assertCanAdministrate,
  assertCanBrowseCatalog,
  assertCanEditCatalogLookups,
} from "./admin-gate";

// The browser's doors onto the catalog's admin lookup surfaces: read and manage
// the three lookup domains, read and manage tiers.
//
// The gate stack is the one `admin-background-jobs.ts` established, for the same
// three reasons: `requireAuth` (no session, no call), a capability assertion
// (`./admin-gate` — a server function is callable without ever rendering the
// route that guards it), and Python's own `require_any_role` plus the verb
// behind it, which remains the enforcement (#67 §4).
//
// **Each handler asserts the capability mirroring the Python gate it fronts, and
// nothing stricter.** `client/docs/architecture.md` ("Authentication &
// authorization") condition 1 is the test — deleting the BFF check must change
// no outcome — so a check narrower than its Python counterpart is the BFF
// deciding authorization, not backing it up. Concretely:
//
//   - lookup lists → Python `_READ_GATE`: no BFF check (see `handleListLookups`),
//   - lookup writes → Python `_EDIT_GATE`: `assertCanEditCatalogLookups`, which
//     admits every editor and not only admins — and so does the manufacturer
//     usage read (#803), gated like the delete it precedes,
//   - tier writes → Python `_ADMIN_GATE`: `assertCanAdministrate` (ADR-0005 §8,
//     admin-only tier administration),
//   - the tier *list* → Python `_TIER_READ_GATE`: `assertCanBrowseCatalog`,
//     every catalog reader — the spec editor's Item Search island reads it for
//     whoever opens the editor (#895, #964 N1), the batch Mark as Core dialog
//     for its national editor (#255).
//
// The earlier draft asserted `canAdministrate` across all seven on the grounds
// that these functions only ever serve the Admin section. Cross-review was right
// that the reasoning does not survive the condition: the *section* being
// admin-only is the route guard's job, and a national editor calling the
// function directly must get the answer Python would give. The per-route gate
// tiers above are pinned in
// `@/server/data/internal-api/catalog-admin-drift.unit.test.ts`, and each
// capability's role set in `@/lib/roles-drift.unit.test.ts`.
//
// Error scrubbing is global function middleware (`start.ts`) — no per-function
// wrapper.
//
// Every handler writes out its own `await import("@/server/data/internal-api/…")`
// rather than sharing a module-scope `() => import(…)` helper. The repetition is
// the rule as `client/docs/architecture.md` (L4, "Must") states it — "pull
// server-only code via dynamic `import()` inside the `.handler(...)`, never at
// module scope" — and a one-line helper is not worth being the file where that
// rule is read loosely. `declarationsFor` below still takes the module as an
// argument, so the seven imports are the only thing repeated.

interface HandlerContext {
  session: ResolvedSession;
}

/**
 * Which four declarations serve a kind.
 *
 * One table keyed by kind, so adding a lookup domain to `@/lib/catalog-admin`
 * fails to compile until its declarations are named here — the same enforcement
 * `ADMIN_TAB_PANELS` uses for tabs. It takes the module rather than importing
 * it: `@/server/data/internal-api` is server-only, so it has to be reached
 * through a dynamic `import()` inside a handler, which rules out a table of
 * eager module-level references.
 */
type LookupModule = typeof import("@/server/data/internal-api/catalog-admin");

const declarationsFor = (kind: LookupKind, module: LookupModule) =>
  ({
    manufacturer: {
      list: module.listInternalManufacturers,
      page: module.listInternalManufacturersPage,
      create: module.createInternalManufacturer,
      rename: module.renameInternalManufacturer,
      remove: module.deleteInternalManufacturer,
    },
    material: {
      list: module.listInternalMaterials,
      page: module.listInternalMaterialsPage,
      create: module.createInternalMaterial,
      rename: module.renameInternalMaterial,
      remove: module.deleteInternalMaterial,
    },
    finish: {
      list: module.listInternalFinishes,
      page: module.listInternalFinishesPage,
      create: module.createInternalFinish,
      rename: module.renameInternalFinish,
      remove: module.deleteInternalFinish,
    },
  })[kind];

async function handleListLookups({
  data,
  context,
}: {
  data: LookupKindInput;
  context: HandlerContext;
}) {
  // No BFF-side role check on this one, deliberately. Python fronts it with
  // `_READ_GATE` — the tier roles, up to and including `spectool.readonly`;
  // `spectool.trade_partner`, `spectool.system` and `spectool.migrator` are
  // outside it — and a BFF check may only ever be *equal* to the gate it fronts
  // (`client/docs/architecture.md`, "Authentication & authorization",
  // condition 1: deleting it must change no outcome). Asserting the admin or
  // the editor capability here would refuse the readonly role Python admits,
  // which is the BFF deciding authorization. The one capability that would
  // mirror `CATALOG_READ_ROLES` is being introduced by the catalog item-list
  // slice (#249) rather than declared twice here; until it lands, `requireAuth`
  // plus Python's gate is the whole check, and Python's gate is the enforcement
  // either way.
  const { list } = declarationsFor(
    data.kind,
    await import("@/server/data/internal-api/catalog-admin"),
  );
  return list(undefined, { sessionId: context.session.sessionId });
}

async function handleListLookupPage({
  data,
  context,
}: {
  data: LookupPageInput;
  context: HandlerContext;
}) {
  // No BFF-side role check, for exactly `handleListLookups`' reason: Python
  // fronts this route with the same `_READ_GATE`, and a BFF check may only ever
  // be equal to the gate it fronts. Asserting anything here would refuse the
  // readonly role Python admits.
  const { page } = declarationsFor(
    data.kind,
    await import("@/server/data/internal-api/catalog-admin"),
  );
  return page(
    { search: data.search, page: data.page, pageSize: data.pageSize },
    { sessionId: context.session.sessionId },
  );
}

/** The manufacturers a `?mfrs=` filter names, retired ones included (#964 N18). */
async function handleManufacturersByIds({
  data,
  context,
}: {
  data: ManufacturersByIdsInput;
  context: HandlerContext;
}) {
  // No BFF-side role check, for `handleListLookups`' reason: Python fronts it
  // with the same `_READ_GATE`.
  const { listInternalManufacturersByIds } =
    await import("@/server/data/internal-api/catalog-admin");
  return listInternalManufacturersByIds(
    { ids: data.ids },
    { sessionId: context.session.sessionId },
  );
}

async function handleCreateLookup({
  data,
  context,
}: {
  data: LookupNameInput;
  context: HandlerContext;
}) {
  assertCanEditCatalogLookups(context.session, "the catalog vocabulary");
  const { create } = declarationsFor(
    data.kind,
    await import("@/server/data/internal-api/catalog-admin"),
  );
  // `kind` is the *dispatch*, not payload: it chose the declaration above, and
  // the wire has no field for it on a write. Sending it would be an extra key
  // Python's `extra="forbid"` contract rejects outright.
  return create({ name: data.name }, { sessionId: context.session.sessionId });
}

async function handleRenameLookup({
  data,
  context,
}: {
  data: LookupRenameInput;
  context: HandlerContext;
}) {
  assertCanEditCatalogLookups(context.session, "the catalog vocabulary");
  const { rename } = declarationsFor(
    data.kind,
    await import("@/server/data/internal-api/catalog-admin"),
  );
  return rename({ id: data.id, name: data.name }, { sessionId: context.session.sessionId });
}

async function handleDeleteLookup({
  data,
  context,
}: {
  data: LookupDeleteInput;
  context: HandlerContext;
}) {
  assertCanEditCatalogLookups(context.session, "the catalog vocabulary");
  const { remove } = declarationsFor(
    data.kind,
    await import("@/server/data/internal-api/catalog-admin"),
  );
  return remove({ id: data.id }, { sessionId: context.session.sessionId });
}

/** What references a manufacturer — the delete confirm's counts (#803). */
async function handleManufacturerUsage({
  data,
  context,
}: {
  data: ManufacturerUsageInput;
  context: HandlerContext;
}) {
  assertCanEditCatalogLookups(context.session, "the catalog vocabulary");
  const { getInternalManufacturerUsage } = await import("@/server/data/internal-api/catalog-admin");
  return getInternalManufacturerUsage({ id: data.id }, { sessionId: context.session.sessionId });
}

async function handleListTiers({ context }: { context: HandlerContext }) {
  // The one tier handler that is *not* admin-only, mirroring Python's
  // `_TIER_READ_GATE` (`CATALOG_TIER_READ_ROLES` — every catalog reader). The
  // spec editor's Item Search island fills its Tier filter from this list for
  // whoever opens the editor, a division editor included (#895; the owner's
  // #555 ruling on N28), and the batch Mark as Core dialog for its national
  // editor (#255). Any narrower capability here would refuse a caller Python
  // serves — the BFF deciding authorization, which once raised the island's
  // filter-options error on every Division Editor's edit page (#964 N1).
  assertCanBrowseCatalog(context.session, "catalog tiers");
  const { listInternalTiers } = await import("@/server/data/internal-api/catalog-admin");
  return listInternalTiers(undefined, { sessionId: context.session.sessionId });
}

async function handleCreateTier({
  data,
  context,
}: {
  data: TierCreateInput;
  context: HandlerContext;
}) {
  assertCanAdministrate(context.session, "catalog tiers");
  const { createInternalTier } = await import("@/server/data/internal-api/catalog-admin");
  // Straight through: the declaration's encoder owns the wire shape, and
  // restating it here would be that job done twice in the layer that does not
  // own it.
  return createInternalTier(data, { sessionId: context.session.sessionId });
}

async function handleUpdateTier({
  data,
  context,
}: {
  data: TierUpdateInput;
  context: HandlerContext;
}) {
  assertCanAdministrate(context.session, "catalog tiers");
  const { updateInternalTier } = await import("@/server/data/internal-api/catalog-admin");
  return updateInternalTier(data, { sessionId: context.session.sessionId });
}

/**
 * The handlers, exported for their unit tests.
 *
 * A `createServerFn` value is not callable as a plain function — invoking it
 * goes through Start's RPC plumbing — so the gate and the kind dispatch would
 * otherwise only be reachable from an end-to-end test. Nothing on the request
 * path imports this object.
 */
export const __test_handlers = {
  handleListLookups,
  handleListLookupPage,
  handleManufacturersByIds,
  handleCreateLookup,
  handleRenameLookup,
  handleDeleteLookup,
  handleManufacturerUsage,
  handleListTiers,
  handleCreateTier,
  handleUpdateTier,
};

// `.validator`, not the deprecated `.inputValidator`. A rejection here is
// scrubbed to the generic message like any other error, which is why every form
// validates with the same schema client-side before it submits.

export const catalogLookups = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .validator(lookupListInputSchema)
  .handler(handleListLookups);

/** A `GET` server function like its unpaged sibling: the browser↔BFF hop is
 * not the HMAC-signed one, so ADR-0008's reason to make a read a POST stops at
 * the internal wire. The *internal* call it makes is the POST. */
export const catalogLookupPage = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .validator(lookupPageInputSchema)
  .handler(handleListLookupPage);

/** A `GET` like the other reads here; the internal call is the signed `POST`
 *  whose ids ride the body (ADR-0008). */
export const catalogManufacturersByIds = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .validator(manufacturersByIdsInputSchema)
  .handler(handleManufacturersByIds);

export const createCatalogLookup = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(lookupNameInputSchema)
  .handler(handleCreateLookup);

export const renameCatalogLookup = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(lookupRenameInputSchema)
  .handler(handleRenameLookup);

export const deleteCatalogLookup = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(lookupDeleteInputSchema)
  .handler(handleDeleteLookup);

/** A `GET` like the other reads here; the internal call is the signed `GET`
 *  whose one input rides the path (ADR-0008). */
export const catalogManufacturerUsage = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .validator(manufacturerUsageInputSchema)
  .handler(handleManufacturerUsage);

export const catalogTiers = createServerFn({ method: "GET" })
  .middleware([requireAuth])
  .handler(handleListTiers);

export const createCatalogTier = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(tierCreateInputSchema)
  .handler(handleCreateTier);

export const updateCatalogTier = createServerFn({ method: "POST" })
  .middleware([requireAuth])
  .validator(tierUpdateInputSchema)
  .handler(handleUpdateTier);
